import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, FileCheck2, Flag, ChevronRight, ChevronDown, Download, Loader2, PackageOpen, PackageCheck } from 'lucide-react';
import CpscTabBar from './CpscTabBar';
// CHA-289 — the R-A/R-B/R-C term-validity rules + constants moved VERBATIM to the shared module so the
// production verification pipeline (functions/productionVerification.js, via the byte-identical server
// twin functions/costSheetRules.js) runs the EXACT same rules. Labels/semantics unchanged.
import { SP27_CUTOFF_ISO, termChecks, discountCheck, HA_PCT_BY_TERM, costBaseFor, FS_TERMS } from '../../../shared/utils/costSheetRules';

/**
 * CostSheetTester — cost-sheet landed-cost validator (CHA-149 v1.5 + CHA-151 v3 + CHA-236 + CHA-251).
 *
 * CHA-251 adds an ANALYSIS-ONLY discount check + term-validity rules layer (it FLAGS rows, it never mutates
 * orders or BQ data). See the CHA-251 section below (discountCheck / termChecks / costSheetChecks) for the
 * field mapping, the discount bands, rules R-A/R-B/R-C, the Step-0 data-integrity guard, and the
 * three-severity legend. It is separate from — and does not change — the landed-cost variance test.
 *
 * Auto-loads rows live from BigQuery (no manual upload) for one of two sub-tabs — Open WIP
 * (`production_open_wip_enriched_csv`) and Received (`production_2025_receipts_enriched_csv`) — via the
 * purpose-built `getCostSheetRows({ tab })` callable, fresh on every open (no cache). It recomputes the
 * SP27 landed cost row-by-row and surfaces variance vs the engine's `n600_cost`. The analysis runs
 * entirely client-side; the only change vs the old flow is the rows SOURCE (BQ-by-tab, not an upload).
 *
 * Scope (canonical: Notion "InMocean Cost Sheet Formulas — SP27 Spec", §1–§8):
 *   • CPLAN — all 4 terms (WI/FS/W3/FS3), TWO bases (CHA-436): total landed cost on FOB × (1 − HA%)
 *     (100% WI/FS, 97% W3/FS3), duty + tariff on nFOBHA (the first-sale value); Swim divs (10/45/60/61/77/78/41);
 *     Acc/Innov (15/16) W3/FS3 only, QC from stored n595_QC per CHA-554; div 42/55 commission 0% + no QC.
 *   • DUTYP / DIMPT — base price = `n300_cost`; div 42/55 collapse to n600 = n300_cost.
 *   • due_date >= 2026-07-01 (SP27 cutoff); pre-cutoff rows excluded + counted (legacy formulas).
 * v3 adds: worked-out-math per-component drilldown (spec calc with values + engine field labels +
 *   color-coded gaps + plain-English summary), multi-select display chips, and CSV export.
 * Out-of-scope rows are counted + reported, never silently dropped.
 */

// Swim divisions (CPLAN all terms + DUTYP + DIMPT). Acc/Innov (15,16): CPLAN W3/FS3 only;
// CHA-554 supersedes the older tracker note by charging their stored n595_QC value.
const SWIM_DIVISIONS = ['10', '45', '60', '61', '77', '78', '41'];
const ACC_INNOV_DIVISIONS = ['15', '16'];
// Div 42/55: commission 0% AND no QC, regardless of cost-sheet type (DUTYP/DIMPT → n600 = n300_cost).
const ZERO_COMM_DIVISIONS = ['42', '55'];
// CHA-475 — BWI. DivisionName on every row is literally "9.BWI(30/35/38)", so these three are one
// business unit. 38 carries no rows in either feed today; it is admitted anyway because the label names
// it and it will populate. DUTYP here is the standard shape — n300 + QC + 7% — and div 30 CPLAN is the
// standard W3/FS3 shape (9%, HA 3%), so neither needs a new formula, only admission to scope.
// Operator ruling 2026-08-20: "QC does apply to BWI" and "the rule is only after 7/1/26" — the SP27 date
// exclusion is what keeps the pre-season rows (5% commission, no QC) out, and it is left untouched.
const BWI_DIVISIONS = ['30', '35', '38'];
const isSwim = (div) => SWIM_DIVISIONS.includes(div);
const isAccInnov = (div) => ACC_INNOV_DIVISIONS.includes(div);
const isZeroComm = (div) => ZERO_COMM_DIVISIONS.includes(div);
const isBwi = (div) => BWI_DIVISIONS.includes(div);
// CHA-475 — QC eligibility is its OWN set, not a synonym for Swim. Before BWI the two coincided, so one
// predicate served both, and `isSwim` silently meant "charges QC" at four call sites while meaning "is in
// scope" at two others. Widening SWIM_DIVISIONS to admit BWI would therefore have changed scope AND the
// CPLAN commission path as a side effect. Splitting them keeps each rule saying one thing.
const isQcEligible = (div) => isSwim(div) || isBwi(div) || isAccInnov(div);
// CHA-450 — the verdict threshold. Operator ruling 2026-08-12: "going forward, pass should be a match —
// if a discount doesn't meet my expectation that is a warning, but the match should match so I can find
// real issues." So this is HALF A CENT ABSOLUTE, not the former 1% relative band.
//
// The spec and the engine compute from the same stored inputs with defined formulas, so if both are right
// they agree to the cent. Measured across 9,336 in-scope CPLAN rows: 96.9% already agree within half a
// cent, and the old 1% band concealed exactly 23 rows — every one the same FS3/SHIPPED/flag-Y defect
// (0.311%–0.998%). It was never absorbing float noise; values are stored at 5dp, so real rounding is
// ~0.00002, three orders of magnitude below what was forgiven.
//
// A RELATIVE band was also the wrong shape: the gap a fixed defect produces scales with the row, so the
// verdict tracked FOB rather than correctness. On PO 1504135, same shipment and same defect, KTS26009
// (FOB 1.72) passed at 0.9985% while TTS26023 (FOB 1.63) failed at 1.0006% — 0.002 percentage points
// apart. Matches COST_EPS in functions/productionVerification.js, so both surfaces judge one threshold.
const MATCH_EPS = 0.005; // half a cent at 3dp (operator ruling, CHA-450)
// CHA-450 (Codex) — the boundary itself needs a representation guard, or it drifts by magnitude. Binary
// floating point puts two rows with the SAME nominal $0.005 variance on opposite sides of it:
//   10.705 − 10.700 = 0.005000000000000782  → fails
//    2.355 −  2.350 = 0.004999999999999893  → passes
// Exactly the defect this PR removed from the relative band — a verdict tracking magnitude rather than
// correctness — reintroduced at the new edge. Same class as the BAND_EPS guard on the 20% discount edge
// (CHA-451) and CHA-314's cents-rounding artifact. A machine epsilon, not a business tolerance.
const MATCH_EPS_FLOAT_GUARD = 1e-9;
// CHA-439 — the assumed first-sale discount while the real price is outstanding. The engine books duty
// and tariff on FOB x 0.85 at issue; measured at FOB x 0.85 on 2,240/2,243 FS and 3,763/3,767 FS3 rows.
const FS_PLACEHOLDER_PCT = 15;

// SP27 date cutoff: only rows due on/after this are validated; earlier rows are legacy (excluded).
// CHA-289 — SP27_CUTOFF_ISO now single-sourced from the shared rules module (imported above).
const SP27_CUTOFF = new Date(`${SP27_CUTOFF_ISO}T00:00:00`);

// v3 display-filter chip groups (multi-select). Division list incl. 42/55 (operator-ratified).
const CHIP_PROD_TYPES = ['CPLAN', 'DUTYP', 'DIMPT'];
const CHIP_TERMS = ['WI', 'FS', 'W3', 'FS3'];
const CHIP_OFFICES = ['NY', 'non-NY'];
const CHIP_STAGES = ['ISSUED', 'SHIPPED']; // CHA-312 — operator: filter by stage
// CHA-475 review (Codex F1) — BWI 30/35/38 added. The divisions became testable in this PR but were
// missing from the filter chips, so an operator selecting a division chip could see BWI rows that the
// selection did not name. A division that is in SCOPE but not in the chip list is invisible to the filter.
export const CHIP_DIVISIONS = ['10', '15', '16', '30', '35', '38', '41', '42', '45', '55', '60', '61', '77', '78'];
const officeOf = (terms) => (terms === 'WI' || terms === 'FS' ? 'NY' : 'non-NY');
// A chip group only filters values that HAVE a chip. A row whose value isn't in the group's
// canonical list (e.g. a DUTYP/DIMPT row with an off-list `terms` such as '09') is never hidden
// by that group — so default all-selected truly shows every tested row (Codex review, PR #55).
const passesChip = (value, all, sel) => !all.includes(value) || sel.includes(value);

// Identifier columns surfaced in the variance table when present (Open WIP shape).
const ID_COLUMNS = [
  { key: 'pod_pkey', label: 'PO / Key' },
  { key: 'style_desc', label: 'Style' },
  { key: 'mg2_customer', label: 'Customer' },
];

// Total table column count (full-width drilldown / empty-state rows):
// 1 chevron + ID_COLUMNS + Div/Type/Terms/Stage(4) + Base(1) + Duty%/Tar%(2) + Exp/Act/Var$/Var%(4) + Result(1).
const COL_SPAN = ID_COLUMNS.length + 13;

// Raw ERP export → number. Tolerant of $, commas, whitespace, blanks.
function num(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[$,\s]/g, '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const s = (v) => String(v ?? '').trim();

// Parse a date cell → Date (or null). Prefers ISO YYYY-MM-DD (the export format — BQ auto-types it as
// DATE); falls back to Date.parse for safety so a format surprise can't mis-filter.
function parseDate(v) {
  const str = s(v);
  if (!str) return null;
  let d;
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) d = new Date(`${str.slice(0, 10)}T00:00:00`);
  else { const t = Date.parse(str); d = Number.isNaN(t) ? null : new Date(t); }
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

// Returns null if the row's `dateField` is on/after the SP27 cutoff, else a skip reason (counted
// separately). Both tabs apply the cutoff, on DIFFERENT columns (CHA-236): Open WIP on `due_date`,
// Received on `trans_date`.
function dateExclusion(r, dateField) {
  const d = parseDate(r[dateField]);
  if (d === null) return `${dateField} missing/unparseable (cannot confirm ≥ ${SP27_CUTOFF_ISO})`;
  return d >= SP27_CUTOFF ? null : `${dateField} before ${SP27_CUTOFF_ISO} (legacy pre-SP27 formulas)`;
}

// Scope filter. Returns null if in scope, else a human skip reason.
//   CPLAN  + Swim/BWI/42/55 → terms WI/FS/W3/FS3 ; CPLAN + Acc/Innov(15,16) → W3/FS3 only
//   DUTYP/DIMPT + Swim/BWI/15-16/42-55 → any terms
function scopeReason(r) {
  const div = s(r.div);
  const prd = s(r.pod_PrdTyp).toUpperCase();
  const terms = s(r.terms).toUpperCase();
  if (prd === 'CPLAN') {
    if (isSwim(div) || isBwi(div) || isZeroComm(div)) {
      return ['WI', 'FS', 'W3', 'FS3'].includes(terms) ? null : `CPLAN div ${div} terms ${terms || 'blank'} (not WI/FS/W3/FS3)`;
    }
    if (isAccInnov(div)) {
      return ['W3', 'FS3'].includes(terms) ? null : `CPLAN div ${div} terms ${terms || 'blank'} (15/16 are W3/FS3 only)`;
    }
    return `CPLAN div ${div || 'blank'} (out of scope)`;
  }
  if (prd === 'DUTYP' || prd === 'DIMPT') {
    return (isSwim(div) || isBwi(div) || isAccInnov(div) || isZeroComm(div)) ? null : `${prd} div ${div || 'blank'} (out of scope)`;
  }
  return `pod_PrdTyp ${prd || 'blank'} (out of scope)`;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// CHA-251 — discount check + term-validity rules. ANALYSIS-ONLY: these FLAG rows, they never mutate
// orders or BQ data. They are a SEPARATE surface from the landed-cost variance test above and do NOT
// touch the existing scope/date/eval logic.
//
// Field mapping (§6.2.1 — verified live against production_open_wip_enriched_csv /
// production_2025_receipts_enriched_csv, both plain-STRING columns):
//   FOB  (full, pre-discount)          = n300_cost   (measured ~15% ABOVE nFOBHA on FS/FS3 → direction right)
//   FOBHA (first-sale-adjusted, lower) = nFOBHA
//   term                               = terms       (WI / FS / W3 / FS3)
//   plan                               = pod_PrdTyp  (CPLAN / DUTYP / DIMPT)
//   stage                              = stage       (Open WIP: SHIPPED; Received table stores RECEIVD)
//   office                             = poh_office  (NY vs non-NY sourcing office — the REAL office, NOT the
//                                                      terms-derived `officeOf`, which would make R-C circular)
//   destination / warehouse            = shp2        (ship-to code; Canada = VBI/CLG/VNI, US = any other)
// The discount uses FOB/FOBHA, NOT n600_cost — so it is independent of the known n600_cost=0 CPLAN defect.
// CHA-289 — CANADA_WAREHOUSES / FS_TERMS / NY_ONLY_TERMS / KNOWN_TERMS / isCanadaWarehouse now live in
// src/shared/utils/costSheetRules.js (imported above), shared with the server-side verification pipeline.
// CHA-380 — ① discountCheck (Rule 1 FOBHA>FOB, bands, fsAtSpec15) MOVED VERBATIM there too (imported
// above), so the verification pipeline's 'FS Discount Errors' tab judges with this exact implementation.

// ② Term-validity rules (R-A / R-B / R-C) — MOVED VERBATIM to src/shared/utils/costSheetRules.js
// (CHA-289; imported above) so the server-side verification pipeline runs the exact same `termChecks`.

// Combined CHA-251 checks for one row → null if the row has no discount-scope result AND no term findings.
export function costSheetChecks(r) {
  const discount = discountCheck(r);
  const termRules = termChecks(r);
  if (!discount && termRules.length === 0) return null;
  return { discount, termRules };
}

// CHA-310 (operator 2026-07-15) — CLEAN = "pass can't have a flag, not a warning":
//   • landed-cost variance PASS, and
//   • zero computeRow flags (FS-discount %, commission-rate drift), and
//   • zero term findings (R-A/R-B/R-C — warnings included), and
//   • the discount band, when in scope, is exactly 'green' (12–20%): yellow/red/error/data-incomplete
//     are problems, and strong-green carries review=true ("verify real") — none of those are clean.
export function isCleanRow(t) {
  if (t.status !== 'pass' || (t.flags || []).length) return false;
  const c = t.checks;
  if (!c) return true;
  if ((c.termRules || []).length) return false;
  // CHA-312 — the shipped-at-spec-15% WARNING disqualifies clean too.
  return !c.discount || (c.discount.band === 'green' && !c.discount.review && !c.discount.fsAtSpec15);
}

// Roll up CHA-251 counts across a set of checked rows (each item's `.checks` may be null). Used for the
// summary strip. Bands + rule severities are counted independently (a row can carry both a discount band
// and a term finding).
export function summarizeChecks(items) {
  const c = {
    dataIncomplete: 0, discountError: 0, red: 0, yellow: 0, green: 0, strongGreen: 0, review: 0,
    fsAtSpec15: 0, // CHA-312 — shipped FS still at the 15% placeholder
    RA: 0, RB: 0, RC: 0, invalid: 0, warning: 0,
  };
  for (const it of items) {
    const ch = it && it.checks;
    if (!ch) continue;
    if (ch.discount) {
      const b = ch.discount.band;
      if (b === 'data-incomplete') c.dataIncomplete += 1;
      else if (b === 'error') c.discountError += 1;
      else if (b === 'red') c.red += 1;
      else if (b === 'yellow') c.yellow += 1;
      else if (b === 'green') c.green += 1;
      else if (b === 'strong-green') c.strongGreen += 1;
      if (ch.discount.review) c.review += 1;
      if (ch.discount.fsAtSpec15) c.fsAtSpec15 += 1; // CHA-312
    }
    for (const tr of (ch.termRules || [])) {
      if (tr.rule === 'R-A') c.RA += 1;
      else if (tr.rule === 'R-B') c.RB += 1;
      else if (tr.rule === 'R-C') c.RC += 1;
      if (tr.severity === 'invalid') c.invalid += 1;
      else if (tr.severity === 'warning') c.warning += 1;
    }
  }
  return c;
}

// Standard display fields (parsed, possibly null → '—' in the table).
function displayFields(r) {
  return {
    nFOBHA: num(r.nFOBHA),
    mst: num(r.mst_qty),
    // CHA-440 — the TRUE piece count. pod_qty is packs on a prepack line, mp_qty the pieces per pack;
    // Total_Units is their product (exact on both feeds). Showing packs as "units" is the CHA-433 defect.
    packs: num(r.pod_qty_num),
    mpQty: num(r.mp_qty_num),
    units: num(r.Total_Units),
    frt: num(r.n301_frt),
    duty: num(r.n401_DutyPct),
    tar: num(r.n316_TarPct),
  };
}

// The engine's per-component output fields, for the §8 spec-vs-engine drilldown.
function engineFields(r) {
  return {
    n300: num(r.n300_cost),       // pre-discount FOB (CPLAN) / contract base price (DUTYP/DIMPT)
    engDuty: num(r.n500_Duty),    // engine duty $
    engTar: num(r.n317_Tariff),   // engine tariff $
    engComm: num(r.n505_AgtCom),  // engine commission $
    engCommPct: num(r.n501_AgtPct), // engine commission rate (whole %)
    // CHA-440 — QC IS stored by the engine (n595_QC) and baked into n600_cost, so it has a real engine
    // value to compare against. It rendered '—' only because the component passed engine: null.
    engQC: num(r.n595_QC),
  };
}

// Enrich each component with delta (engine − spec; +higher / −lower) + gap classification, and
// generate the plain-English narrative. "main" = largest comparable |delta|; "small" = ≥1% of the
// engine total; "match" = comparable but negligible; "na" = no engine value to compare (FOBHA/QC).
const GAP_EPS = 0.005; // sub-half-cent at 3dp = effectively a match
function analyze(components, actual, expected) {
  const enriched = components.map((c) => {
    const comparable = c.engine !== null && c.engine !== undefined && c.spec !== null && c.spec !== undefined;
    return { ...c, comparable, delta: comparable ? c.engine - c.spec : null };
  });
  let mainKey = null;
  let mainAbs = 0;
  for (const c of enriched) {
    if (!c.comparable) continue;
    const a = Math.abs(c.delta);
    if (a > mainAbs) { mainAbs = a; mainKey = c.key; }
  }
  const denom = Math.abs(actual) > 0 ? Math.abs(actual) : 1;
  const classified = enriched.map((c) => {
    let gapClass = 'na';
    if (c.comparable) {
      const a = Math.abs(c.delta);
      if (c.key === mainKey && a > GAP_EPS) gapClass = 'main';
      else if (a / denom >= 0.01) gapClass = 'small';
      else gapClass = 'match';
    }
    return { ...c, gapClass };
  });
  return { components: classified, summary: narrative(classified, actual, expected) };
}

const cleanLabel = (l) => l.replace(/\s*\(.*\)$/, '');

function narrative(components, actual, expected) {
  const totalVar = actual - expected;
  if (Math.abs(totalVar) < GAP_EPS) return 'Matches spec within rounding — no material component gap.';
  const gaps = components
    .filter((c) => c.comparable && Math.abs(c.delta) > GAP_EPS)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  if (!gaps.length) {
    return `Engine total is $${fmt3(Math.abs(totalVar))} ${totalVar > 0 ? 'higher' : 'lower'} than spec, but the gap isn't in a separately-stored line (likely FOBHA or QC, which the engine doesn't break out).`;
  }
  const top = gaps[0];
  const share = Math.round((Math.abs(top.delta) / Math.abs(totalVar)) * 100);
  let txt = `${cleanLabel(top.label)} drives ${share}% of the gap (engine is $${fmt3(Math.abs(top.delta))} ${top.delta > 0 ? 'higher' : 'lower'} than spec).`;
  const second = gaps[1];
  if (second) {
    const share2 = Math.round((Math.abs(second.delta) / Math.abs(totalVar)) * 100);
    if (share2 >= 10) txt += ` ${cleanLabel(second.label)} accounts for ${share2}%.`;
  }
  return txt;
}

function buildResult({ expected, actual, display, components, flags }) {
  const variance = actual - expected;
  // relVar is retained for DISPLAY and for worst-first sorting only — it no longer decides the verdict.
  // Guard divide-by-zero: a zero expected only "passes" on an exact zero variance.
  const relVar = expected === 0 ? (variance === 0 ? 0 : Infinity) : Math.abs(variance) / Math.abs(expected);
  const { components: analyzed, summary } = analyze(components, actual, expected);
  return {
    // CHA-450 — pass means the spec and the engine AGREE, not that they are within 1% of each other.
    // Operator ruling 2026-08-12: "pass should be a match ... so I can find real issues."
    // CHA-450 (Codex) — a ZERO expected still passes only on an EXACT zero variance. The old relative
    // comparison enforced that for free (relVar became Infinity); an absolute epsilon does not, and would
    // let an engine cost of up to half a cent masquerade as a match against a spec of nothing. Reachable:
    // computeDutyp/computeDimpt guard only `n300 === null`, not `<= 0` as CPLAN does, so a zero-priced
    // DUTYP row on div 42/55 (commission 0%, non-swim so QC 0) computes expected === 0 exactly.
    status: expected === 0
      ? (variance === 0 ? 'pass' : 'fail')
      : (Math.abs(variance) <= MATCH_EPS + MATCH_EPS_FLOAT_GUARD ? 'pass' : 'fail'),
    ...display, actual, expected, variance, relVar,
    components: analyzed, summary, flags: flags || [],
  };
}

// Dispatcher: one cost-sheet template per pod_PrdTyp. scopeReason already gated div/terms.
// Canonical formulas: Notion SP27 spec §1–§4.
function computeRow(r) {
  switch (s(r.pod_PrdTyp).toUpperCase()) {
    case 'CPLAN': return computeCplan(r);
    case 'DUTYP': return computeDutyp(r);
    case 'DIMPT': return computeDimpt(r);
    default: return { status: 'error', reason: `unhandled cost-sheet type ${s(r.pod_PrdTyp) || 'blank'}` };
  }
}

// §1/§2 — CPLAN (FOB-based). Cost base = n300_cost × (1 − HA%) — 100% on WI/FS, 97% on W3/FS3 (CHA-436).
// D/T base = nFOBHA (the first-sale value; the FS discount is already baked in at upload, so no second
// × 0.85 here — CHA-153 double-discount fix). Commission base = n300_cost, matching the engine + DUTYP.
// QC for Swim/BWI plus Acc/Innov at their stored rate; commission 7% (WI/FS) / 9% (W3/FS3), 0% for div 42/55.
function computeCplan(r) {
  const f = displayFields(r);
  const e = engineFields(r);
  const ifrt = num(r.n305_Ifrt) ?? 0; // inland freight (already includes the $0.02 Swim premium)
  const afrt = num(r.n304_Afrt) ?? 0; // air freight (0 for ocean)
  const actual = num(r.n600_cost);

  // CHA-436 — a zeroed/negative FOB or FOBHA is BAD DATA, not a cheap input, and both are now
  // load-bearing: n300_cost drives the cost base and the commission, nFOBHA drives duty + tariff.
  // Before CHA-436 a zeroed nFOBHA collapsed the whole spec total, so the row always blew the 1%
  // tolerance and announced itself; now it only zeroes duty+tariff, which can land INSIDE tolerance and
  // read as a pass (self-review). Guarding ≤ 0 rather than just null keeps the module's own stance —
  // costSheetRules.js:129: "a zeroed field must never masquerade". 11 live CPLAN rows are affected.
  const missing = [];
  if (f.nFOBHA === null) missing.push('nFOBHA');
  else if (f.nFOBHA <= 0) missing.push('nFOBHA (≤ 0 — prices are always > 0)');
  if (f.frt === null) missing.push('n301_frt');
  if (f.duty === null) missing.push('n401_DutyPct');
  if (f.tar === null) missing.push('n316_TarPct');
  if (actual === null) missing.push('n600_cost');
  if (e.n300 === null) missing.push('n300_cost'); // CHA-155: commission base; CHA-436: also the cost base
  else if (e.n300 <= 0) missing.push('n300_cost (≤ 0 — prices are always > 0)');
  if (missing.length) return { status: 'error', reason: `missing/non-numeric: ${missing.join(', ')}`, ...f, actual };

  const terms = s(r.terms).toUpperCase();
  const stage = s(r.stage).toUpperCase();
  const div = s(r.div);
  // CHA-436 — TWO BASES, not one. The module previously used a single FOBHA basis for all four terms,
  // which is right for WI/W3 only by COINCIDENCE (there nFOBHA already equals FOB × (1 − HA%)) and wrong
  // for FS/FS3, where the same field carries the ~15% first-sale discount. Using it as the cost base made
  // the spec reproduce the engine's own error, so every FS/FS3 row scored `pass` — the validator was
  // certifying the defect it exists to catch (CHA-435).
  //
  //   TOTAL LANDED COST base = FOB × (1 − HA%)   →  100% on WI/FS, 97% on W3/FS3
  //   DUTY and TARIFF base   = nFOBHA            →  the first-sale customs value, ~15% off on FS/FS3
  //
  // Operator ruling 2026-08-10, verbatim: "the Issued stage same as shipped stage and received - for the
  // based FOB that should be used for the total landed cost should be 100% of the FOB in FS or 97% in
  // FS3 - like we do in WI and W3 - the only change is that in FS and FS3 during issued we assume it
  // will have 15% discount of traiff /duty but shouldnot be on total cost."
  //
  // Reading the two halves together: "during issued" describes WHEN the 15% is an assumption rather than
  // a measured value — at booking the actual first-sale price isn't back yet, so the 15% is a
  // placeholder (see the CHA-312 fsAtSpec15 warning below). It does NOT scope the rule to that stage:
  // the ruling opens by putting ISSUED, SHIPPED and RECEIVED on the same footing. So the cost base is
  // STAGE-INDEPENDENT and nothing below branches on stage; the 15% is a duty/tariff input at every
  // stage, and a cost reduction at none.
  //
  // HA% is derived from the TERM because this module encodes the spec and is judged against the engine;
  // deriving it from the engine's own n309_HApct would let a wrong engine value define the spec it is
  // being tested against. The two agree on all 6,556 CPLAN rows today (WI/FS 0%, W3/FS3 3%), and it is
  // the same 0/3 split as the commission rate below — the "3" in W3/FS3 IS the HA deduction.
  const haPct = HA_PCT_BY_TERM(terms);   // CHA-437 — shared with the verification pipeline
  // CHA-439 — the DUTY/TARIFF base. Two states, per the operator ruling 2026-08-10:
  //   flag OFF (issued) — the real first-sale price is not back yet, so the booking PLACEHOLDER applies:
  //                       FOB x 0.85 (the 15% first-sale discount).
  //   flag ON  (shipped) — the actual first-sale price has arrived; duty and tariff are assessed on it.
  // It is NEVER nFOBHA. Until 2026-08-10 nFOBHA held the first-sale value, so reading it worked by
  // accident; the ERP then repurposed that column as the COST base, and every FS/FS3 row started failing
  // on the duty line (CHA-438). Deriving it from nFOBHA again would re-break the moment the ERP finishes
  // fixing its shipped rows, where nFOBHA is still overwritten with fs_cost.
  //
  // fs_cost is used for duty/tariff ONLY — never for the landed cost, which stays on costBase above.
  const agentFOBHA = e.n300;                    // pre-discount FOB — the commission base (CHA-155)
  const costBase = costBaseFor(agentFOBHA, terms);  // what we actually pay — the landed-cost base
  const fsCost = num(r.fs_cost);
  const fsOn = s(r.fs_flag).toUpperCase() === 'Y' && fsCost !== null && fsCost > 0;
  // The 15% placeholder is a FIRST-SALE concession — it applies to FS/FS3 only. On WI/W3 there is no
  // first-sale discount at all, so duty and tariff are assessed on the same base the cost is: measured at
  // 1.00 x FOB on WI and 0.97 x FOB on W3, i.e. exactly costBase.
  // The flag override is gated on the TERM as well (CodeRabbit): a stale or mispopulated fs_flag on a
  // WI/W3 row would otherwise assess duty on a first-sale price those terms never receive.
  const isFirstSale = FS_TERMS.includes(terms);
  const dutyTariffBase = (isFirstSale && fsOn) ? fsCost
    : (isFirstSale ? agentFOBHA * (1 - FS_PLACEHOLDER_PCT / 100) : costBase);
  const commRate = isZeroComm(div) ? 0 : ((terms === 'WI' || terms === 'FS') ? 0.07 : 0.09);
  const qc = isQcEligible(div) ? (num(r.n595_QC) ?? 0) : 0; // CHA-156: QC = stored n595_QC (rate × pack qty), not a flat constant; CHA-475/554: Swim + BWI + 15/16 charge it, 42/55 stay at 0

  const duty = dutyTariffBase * (f.duty / 100);
  const tariff = dutyTariffBase * (f.tar / 100);
  const commission = agentFOBHA * commRate;
  const expected = costBase + duty + tariff + f.frt + ifrt + afrt + qc + commission;

  // §8 per-component breakdown — `calc` is the worked-out spec math (values plugged in), `engineField`
  // names the engine column. FRT/IFRT/AFRT come straight from the same field (engine == spec). The cost
  // base and QC have no comparable engine column (the engine never breaks the base out separately), so
  // both render '—' in the engine column; QC's stored field (n595_QC) is surfaced in CHA-152.
  // Name the duty/tariff base in the drilldown so the operator can see WHICH state the row is in — the
  // placeholder, the real first-sale price, or (WI/W3) the cost base itself.
  const dtSource = (isFirstSale && fsOn) ? `FS cost $${fmt3(fsCost)}`
    : (isFirstSale ? `FOB $${fmt3(agentFOBHA)} × ${100 - FS_PLACEHOLDER_PCT}%` : `cost base $${fmt3(costBase)}`);
  const commLabel = isZeroComm(div) ? 'Commission (0% — div 42/55)' : `Commission (${(commRate * 100).toFixed(0)}%)`;
  const components = [
    // CHA-436 — the cost base is FOB × (1 − HA%), NOT nFOBHA. The parenthetical in each label is
    // STRIPPED by cleanLabel() before render (:258), so the basis reaches the operator through the
    // `calc` column, which shows the worked math with the actual base plugged in. The labels keep the
    // annotation for the source reader only.
    { key: 'fobha', label: `Cost base (FOB${haPct ? ` × ${100 - haPct}%` : ''})`, calc: `n300_cost $${fmt3(agentFOBHA)}${haPct ? ` × ${100 - haPct}%` : ''} = $${fmt3(costBase)}`, spec: costBase, engine: null, engineField: 'n300_cost' },
    { key: 'duty', label: 'Duty', calc: `${dtSource} × ${f.duty} / 100 = $${fmt3(duty)}`, spec: duty, engine: e.engDuty, engineField: 'n500_Duty' },
    { key: 'tariff', label: 'Tariff', calc: `${dtSource} × ${f.tar} / 100 = $${fmt3(tariff)}`, spec: tariff, engine: e.engTar, engineField: 'n317_Tariff' },
    { key: 'frt', label: 'FRT (ocean)', calc: `n301_frt = $${fmt3(f.frt)}`, spec: f.frt, engine: f.frt, engineField: 'n301_frt' },
    { key: 'ifrt', label: 'IFRT (inland)', calc: `n305_Ifrt = $${fmt3(ifrt)}`, spec: ifrt, engine: ifrt, engineField: 'n305_Ifrt' },
    { key: 'afrt', label: 'AFRT (air)', calc: `n304_Afrt = $${fmt3(afrt)}`, spec: afrt, engine: afrt, engineField: 'n304_Afrt' },
    { key: 'qc', label: 'QC', calc: isQcEligible(div) ? `n595_QC = $${fmt3(qc)}` : 'n/a (div 42/55)', spec: qc, engine: e.engQC, engineField: 'n595_QC' },
    { key: 'commission', label: commLabel, calc: `n300_cost $${fmt3(agentFOBHA)} × ${(commRate * 100).toFixed(0)}% = $${fmt3(commission)}`, spec: commission, engine: e.engComm, engineField: 'n505_AgtCom' },
  ];

  const flags = [];
  // §7.1 — FS/FS3 SHIPPED: the first-sale value should be ≥15% off the pre-discount FOB (n300_cost).
  //
  // CHA-451 — measured against fs_cost once the flag is on, NOT nFOBHA. Since CHA-438 the ERP uses nFOBHA
  // as the COST base, where on FS3 it carries only the 3% HA deduction, so comparing FOB to it reports
  // "3% discount" on a line that actually earned ~20%. pod_pkey 1349569: FOB 1.72 vs nFOBHA 1.66840 reads
  // 3.0%, while fs_cost 1.376 reads 20.0%. Every flagged row on the 2026-08-12 export carried this
  // warning undeservedly. Operator ruling the same day: a disappointing discount is a WARNING and the
  // match is a separate axis — so this must measure the right pair or the warning means nothing.
  // Flag off → no arrived price → nFOBHA stays the best available signal. Mirrors discountCheck in the
  // shared costSheetRules twins, which judges the same property for the verification sheet.
  const discBase = fsOn ? fsCost : f.nFOBHA;
  if (['FS', 'FS3'].includes(terms) && stage === 'SHIPPED' && e.n300 !== null && e.n300 !== 0 && discBase !== null) {
    const disc = (e.n300 - discBase) / e.n300;
    // CHA-314 (operator ruling 2026-07-15) — 'discount may not be honored' fires only BELOW 14%. A row
    // at ~15% (incl. the 14.97% cents-rounding of the placeholder) is 'at spec 15%' (the CHA-312 shipped
    // warning covers it); the old no-tolerance '<15%' flag fired on a rounding artifact and rounded its
    // own label back to '15.0%' (self-contradictory). 2-decimal label for honesty.
    if (disc < 0.14) flags.push({ key: 'fs15', label: `FS discount only ${(disc * 100).toFixed(2)}% (< 14% — discount may not be honored)` });
  }
  // Commission-rate cross-check (engine n501_AgtPct vs spec). Suppressed for div 42/55 (spec 0% by rule).
  if (!isZeroComm(div) && e.engCommPct !== null && Math.abs(e.engCommPct - commRate * 100) > 0.001) {
    flags.push({ key: 'commrate', label: `Commission rate: spec ${(commRate * 100).toFixed(0)}% vs engine ${e.engCommPct}%` });
  }

  return buildResult({ expected, actual, display: { ...f, base: costBase }, components, flags });
}

// §3 — DUTYP (DDP): supplier absorbs duty/tariff/freight. Base = n300_cost. QC (Swim) + 7% commission
// (0% for div 42/55, which collapses to n600 = n300_cost).
function computeDutyp(r) {
  const f = displayFields(r);
  const e = engineFields(r);
  const actual = num(r.n600_cost);
  if (e.n300 === null || actual === null) {
    const m = [e.n300 === null && 'n300_cost', actual === null && 'n600_cost'].filter(Boolean).join(', ');
    return { status: 'error', reason: `missing/non-numeric: ${m}`, ...f, actual };
  }
  const div = s(r.div);
  const qcEligible = isQcEligible(div); // CHA-475 — Swim + BWI, not Swim membership
  const qc = qcEligible ? (num(r.n595_QC) ?? 0) : 0; // CHA-156: stored n595_QC, not flat $0.03
  const commRate = isZeroComm(div) ? 0 : 0.07;
  const commission = e.n300 * commRate;
  const expected = e.n300 + qc + commission;

  const commLabel = isZeroComm(div) ? 'Commission (0% — div 42/55)' : 'Commission (7%)';
  const components = [
    { key: 'base', label: 'Base (n300_cost)', calc: `n300_cost = $${fmt3(e.n300)}`, spec: e.n300, engine: null, engineField: 'n300_cost' },
    ...(qcEligible ? [{ key: 'qc', label: 'QC', calc: `n595_QC = $${fmt3(qc)}`, spec: qc, engine: e.engQC, engineField: 'n595_QC' }] : []),
    { key: 'commission', label: commLabel, calc: `$${fmt3(e.n300)} × ${(commRate * 100).toFixed(0)}% = $${fmt3(commission)}`, spec: commission, engine: e.engComm, engineField: 'n505_AgtCom' },
  ];
  const flags = [];
  if (!isZeroComm(div) && e.engCommPct !== null && Math.abs(e.engCommPct - 7) > 0.001) {
    flags.push({ key: 'commrate', label: `Commission rate: spec 7% vs engine ${e.engCommPct}%` });
  }
  return buildResult({ expected, actual, display: { ...f, base: e.n300 }, components, flags });
}

// §4 — DIMPT (Direct Import): customer is importer of record. Base = n300_cost. No commission,
// no duty/tariff/freight. QC for Swim/BWI plus Acc/Innov; div 42/55 return n300_cost as-is.
function computeDimpt(r) {
  const f = displayFields(r);
  const e = engineFields(r);
  const actual = num(r.n600_cost);
  if (e.n300 === null || actual === null) {
    const m = [e.n300 === null && 'n300_cost', actual === null && 'n600_cost'].filter(Boolean).join(', ');
    return { status: 'error', reason: `missing/non-numeric: ${m}`, ...f, actual };
  }
  const div = s(r.div);
  const qcEligible = isQcEligible(div); // CHA-475 — Swim + BWI, not Swim membership
  const qc = qcEligible ? (num(r.n595_QC) ?? 0) : 0; // CHA-156: stored n595_QC, not flat $0.03
  const expected = e.n300 + qc;
  // DIMPT spec has NO commission. Surface the engine's commission line (spec 0) so the drilldown shows
  // it when the engine applies one anyway (the DIMPT 15/16 drift), and flag it — EXCEPT div 42/55 (0%
  // is the rule; engine 0 == spec, no false drift).
  const commLabel = isZeroComm(div) ? 'Commission (0% — div 42/55)' : 'Commission (spec 0)';
  const components = [
    { key: 'base', label: 'Base (n300_cost)', calc: `n300_cost = $${fmt3(e.n300)}`, spec: e.n300, engine: null, engineField: 'n300_cost' },
    ...(qcEligible ? [{ key: 'qc', label: 'QC', calc: `n595_QC = $${fmt3(qc)}`, spec: qc, engine: e.engQC, engineField: 'n595_QC' }] : []),
    { key: 'commission', label: commLabel, calc: 'spec = $0.000 (no commission)', spec: 0, engine: e.engComm, engineField: 'n505_AgtCom' },
  ];
  const flags = [];
  if (!isZeroComm(div) && ((e.engCommPct !== null && e.engCommPct !== 0) || (e.engComm !== null && e.engComm !== 0))) {
    const rate = e.engCommPct !== null && e.engCommPct !== 0 ? `${e.engCommPct}%` : `$${fmt3(e.engComm)}`;
    flags.push({ key: 'dimpt-comm', label: `DIMPT spec has no commission, but engine applies ${rate}` });
  }
  return buildResult({ expected, actual, display: { ...f, base: e.n300 }, components, flags });
}

// CHA-236 sub-tabs. Both orange-active (matches the existing Production Verification strip); each maps
// to the enriched table getCostSheetRows reads. `tab` id is the callable's argument.
const TABS = [
  { id: 'open_wip', label: 'Open WIP', icon: PackageOpen, color: 'orange' },
  { id: 'received', label: 'Received', icon: PackageCheck, color: 'orange' },
];

// Evaluate every row → date-excluded (legacy) / out-of-scope / tested. Extracted from the component so
// the analysis core is unit-testable (CHA-236 §7). Both tabs apply the SP27 date cutoff, on DIFFERENT
// columns (`dateField`): Open WIP on `due_date`, Received on `trans_date`. All other math is unchanged.
export function evaluateRows(rows, { dateField = 'due_date' } = {}) {
  if (!rows) return null;
  const tested = [];
  const skipped = [];
  const dateExcluded = [];
  rows.forEach((r, i) => {
    const dateReason = dateExclusion(r, dateField);
    if (dateReason) { dateExcluded.push({ i, reason: dateReason }); return; }
    const reason = scopeReason(r);
    // CHA-251 checks run on every non-date-excluded row (their scopes are SEPARATE from the landed-cost
    // scope — e.g. R-A/R-B/R-C can apply to a row that is out-of-scope for the variance test). Analysis-only.
    const checks = costSheetChecks(r);
    if (reason) { skipped.push({ i, row: r, reason, checks }); return; }
    tested.push({ i, row: r, ...computeRow(r), checks });
  });
  tested.sort((a, b) => (b.relVar ?? -1) - (a.relVar ?? -1)); // worst variance first
  const counts = {
    total: rows.length,
    tested: tested.length,
    clean: tested.filter(isCleanRow).length,
    pass: tested.filter((t) => t.status === 'pass').length,
    fail: tested.filter((t) => t.status === 'fail').length,
    error: tested.filter((t) => t.status === 'error').length,
    flagged: tested.filter((t) => t.flags && t.flags.length > 0).length,
    skipped: skipped.length,
    dateExcluded: dateExcluded.length,
  };
  // CHA-251 rollup across tested + skipped (term rules have separate scopes; a term finding can land on an
  // out-of-landed-cost-scope row). Analysis-only counts — nothing is written back.
  const checkCounts = summarizeChecks([...tested, ...skipped]);
  return { tested, skipped, dateExcluded, counts, checkCounts };
}

export default function CostSheetTester() {
  const [activeTab, setActiveTab] = useState('open_wip'); // 'open_wip' | 'received'
  const [rows, setRows] = useState(null); // raw BQ rows for the active tab (array of objects)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | clean | pass | fail | error
  // CHA-311 — one active check-pill filter at a time (click toggles): {kind:'band'|'rule', value} | null.
  // The discount check only exists on CPLAN FS/FS3 rows, so band pills ARE the First-Sale zoom.
  const [checkFilter, setCheckFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // row index whose drilldown is open
  const [chips, setChips] = useState(defaultChips); // v3 display-filter chip selections
  const reqIdRef = useRef(0); // guards against a stale response landing after a fast tab switch

  const activeLabel = TABS.find((t) => t.id === activeTab)?.label || activeTab;

  // Live BQ fetch for one tab — fresh every open, no cache. Filter/chip/statusFilter state is
  // deliberately NOT reset here, so it survives a sub-tab switch (only the rows refetch). §8.3: a
  // failure sets an error + retry, never a silent skip.
  const load = useCallback(async (tab) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    setRows(null);
    setExpandedId(null);
    try {
      const functions = getFunctions();
      // Explicit 150s client timeout (120s server + 30s transport headroom): the JS SDK's default callable deadline is 70s, but the backend runs up to
      // 120s and a cold BQ read of the ~24k-row Received table can finish in 70–120s — the default would
      // fail in the browser with deadline-exceeded. Match the backend's timeoutSeconds.
      const getCostSheetRows = httpsCallable(functions, 'getCostSheetRows', { timeout: 150000 });
      const res = await getCostSheetRows({ tab });
      if (reqId !== reqIdRef.current) return; // a newer request superseded this one
      // Columnar transport (CHA-236): the callable returns { columns, rows: [[...]] } to stay under the
      // Firebase callable ~10 MB response limit (Received is 11.72 MB as objects, 5.47 MB columnar). Rebuild
      // the array-of-objects the analysis expects at THIS boundary — everything downstream (evaluateRows,
      // filters, exportCsv) is unchanged.
      const data = res?.data || {};
      const cols = data.columns;
      const packed = data.rows || [];
      const objRows = Array.isArray(cols)
        ? packed.map((r) => Object.fromEntries(cols.map((c, i) => [c, r[i]])))
        : packed; // fallback: already object-form
      setRows(objRows);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setError(e?.message || 'Failed to load rows from BigQuery.');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  // Auto-load on mount + whenever the active sub-tab changes.
  useEffect(() => { load(activeTab); }, [activeTab, load]);

  // Switch sub-tab. Drop the previous tab's rows synchronously so the results block never renders the
  // OLD rows under the NEW tab's date-exclusion rule for the one frame before the load effect fires.
  const selectTab = useCallback((id) => {
    if (id === activeTab) return;
    setRows(null);
    // CHA-311 (review fold): the pill zoom clears on tab switch — unlike the chips/status filter, its only
    // visible indicator (the pressed ring + note) lives in a strip that can vanish when the new tab has
    // zero checks, which would leave an unexplained empty table with no way to clear it.
    setCheckFilter(null);
    setActiveTab(id);
  }, [activeTab]);

  // Evaluate the loaded rows. Both tabs apply the SP27 date cutoff, on different columns: Open WIP on
  // due_date, Received on trans_date (CHA-236 — reverses the earlier full-history behavior for Received).
  const dateField = activeTab === 'received' ? 'trans_date' : 'due_date';
  const evaluated = useMemo(
    () => evaluateRows(rows, { dateField }),
    [rows, dateField]
  );

  // Apply status filter (summary cards) AND the four display-chip groups (OR within, AND across).
  const visibleRows = useMemo(() => {
    if (!evaluated) return [];
    return evaluated.tested.filter((t) => {
      // CHA-310 — 'clean' is stricter than 'pass': zero flags, zero term findings, clean discount band.
      if (statusFilter === 'clean') {
        if (!isCleanRow(t)) return false;
      } else if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      // CHA-311 — active check pill: band pills match the row's discount band; rule pills match a term finding.
      if (checkFilter) {
        const c = t.checks;
        if (checkFilter.kind === 'band') {
          if (!c || !c.discount || c.discount.band !== checkFilter.value) return false;
        } else if (checkFilter.kind === 'rule') {
          if (!c || !(c.termRules || []).some((f) => f.rule === checkFilter.value)) return false;
        } else if (checkFilter.kind === 'fsSpec') {
          if (!c || !c.discount || !c.discount.fsAtSpec15) return false; // CHA-312 — the shipped-@15% zoom
        }
      }
      const div = s(t.row.div);
      const type = s(t.row.pod_PrdTyp).toUpperCase();
      const terms = s(t.row.terms).toUpperCase();
      return passesChip(type, CHIP_PROD_TYPES, chips.type)
        && passesChip(terms, CHIP_TERMS, chips.terms)
        && passesChip(officeOf(terms), CHIP_OFFICES, chips.office)
        && passesChip(div, CHIP_DIVISIONS, chips.div)
        && passesChip(s(t.row.stage).toUpperCase(), CHIP_STAGES, chips.stage); // CHA-312 (off-list stages never hidden)
    });
  }, [evaluated, statusFilter, chips, checkFilter]);

  const toggleChip = (group, val) => setChips((p) => ({
    ...p, [group]: p[group].includes(val) ? p[group].filter((x) => x !== val) : [...p[group], val],
  }));
  const resetChips = () => setChips(defaultChips());
  const chipsDirty = useMemo(() => JSON.stringify(chips) !== JSON.stringify(defaultChips()), [chips]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Cost Sheet Tester</h3>
        <p className="text-sm text-slate-500">
          Auto-loads the <strong>{activeLabel}</strong> table live from BigQuery and validates landed cost
          (<code>n600_cost</code>) against the SP27 formula. <strong>Open WIP</strong> tests rows with
          <code>due_date</code> ≥ <strong>{SP27_CUTOFF_ISO}</strong>; <strong>Received</strong> tests rows
          with <code>trans_date</code> ≥ <strong>{SP27_CUTOFF_ISO}</strong> (earlier = legacy, excluded).
          Click a row to see the worked-out per-component math vs the engine.
        </p>
      </div>

      <CpscTabBar tabs={TABS} activeId={activeTab} onSelect={selectTab} />

      {loading && (
        <div role="status" className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin text-orange-600" /> Loading {activeLabel} rows from BigQuery…
        </div>
      )}

      {error && !loading && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-sm text-red-700 flex flex-col items-start gap-2">
          <div className="flex items-start gap-1">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>Could not load {activeLabel} rows: {error}</span>
          </div>
          <button
            onClick={() => load(activeTab)}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700"
          >
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      {evaluated && !loading && !error && (
        <>
          {/* Loaded-rows header */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <FileCheck2 size={16} className="text-orange-600" />
              <span className="font-medium text-slate-800" data-testid="cost-sheet-rowcount">
                Loaded {evaluated.counts.total} rows from {activeLabel} (live from BigQuery)
              </span>
            </div>
            <button onClick={() => load(activeTab)} className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
            <SummaryCard label="Rows" value={evaluated.counts.total} onClick={() => setStatusFilter('all')} active={statusFilter === 'all'} />
            <SummaryCard label="Tested" value={evaluated.counts.tested} tone="slate" />
            {/* CHA-310 — CLEAN: pass with ZERO flags/warnings (stricter than Pass, which is variance-only) */}
            <SummaryCard label="Clean" value={evaluated.counts.clean} tone="green" onClick={() => setStatusFilter('clean')} active={statusFilter === 'clean'} />
            <SummaryCard label="Pass" value={evaluated.counts.pass} tone="green" onClick={() => setStatusFilter('pass')} active={statusFilter === 'pass'} />
            <SummaryCard label="Fail" value={evaluated.counts.fail} tone="red" onClick={() => setStatusFilter('fail')} active={statusFilter === 'fail'} />
            <SummaryCard label="Flagged" value={evaluated.counts.flagged} tone="amber" />
            <SummaryCard label="Pre-7/1 legacy" value={evaluated.counts.dateExcluded} tone="slate" />
          </div>
          {evaluated.counts.error > 0 && (
            <div className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle size={13} /> {evaluated.counts.error} in-scope row(s) could not be computed (bad/missing data) — shown as “Data error” below.
            </div>
          )}

          {/* CHA-251 — discount + term-validity checks (analysis-only; these FLAG rows, never mutate data).
              CHA-311 — the pills are click-to-zoom filters over the tested table. */}
          <ChecksSummary c={evaluated.checkCounts} active={checkFilter} onSelect={(f) => setCheckFilter((p) => (p && f && p.kind === f.kind && p.value === f.value ? null : f))} />

          {/* v3 display-filter chips + CSV export */}
          {evaluated.tested.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-500">Filter results</span>
                <div className="flex items-center gap-3">
                  {chipsDirty && (
                    <button onClick={resetChips} className="text-xs text-blue-600 hover:text-blue-800">Reset filters</button>
                  )}
                  <button
                    onClick={() => exportCsv(visibleRows)}
                    disabled={visibleRows.length === 0}
                    title="Export current view as CSV (respects filters)"
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Download size={13} /> Export CSV
                  </button>
                </div>
              </div>
              <ChipGroup label="Type" group="type" values={CHIP_PROD_TYPES} sel={chips.type} onToggle={toggleChip} />
              <ChipGroup label="Terms" group="terms" values={CHIP_TERMS} sel={chips.terms} onToggle={toggleChip} />
              <ChipGroup label="Office" group="office" values={CHIP_OFFICES} sel={chips.office} onToggle={toggleChip} />
              <ChipGroup label="Div" group="div" values={CHIP_DIVISIONS} sel={chips.div} onToggle={toggleChip} />
              <ChipGroup label="Stage" group="stage" values={CHIP_STAGES} sel={chips.stage} onToggle={toggleChip} />
              <div className="text-xs text-slate-500">Showing {visibleRows.length} of {evaluated.counts.tested} validated rows</div>
            </div>
          )}

          {/* Variance table */}
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th />
                  {ID_COLUMNS.map((c) => <Th key={c.key}>{c.label}</Th>)}
                  <Th>Div</Th><Th>Type</Th><Th>Terms</Th><Th>Stage</Th>
                  <Th right>Base</Th>
                  <Th right>Units</Th>
                  <Th right>Duty%</Th><Th right>Tar%</Th>
                  <Th right>Expected</Th><Th right>Actual</Th><Th right>Var $</Th><Th right>Var %</Th>
                  <Th>Result</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((t) => {
                  const open = expandedId === t.i;
                  const canExpand = t.status !== 'error' && Array.isArray(t.components);
                  return (
                    <React.Fragment key={t.i}>
                      <tr
                        onClick={() => canExpand && setExpandedId(open ? null : t.i)}
                        className={`${canExpand ? 'cursor-pointer' : ''} ${t.status === 'fail' ? 'bg-red-50/40' : t.status === 'error' ? 'bg-amber-50/40' : ''} hover:bg-slate-50`}
                      >
                        <Td>{canExpand ? (open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-300" />) : null}</Td>
                        {ID_COLUMNS.map((c) => <Td key={c.key}>{s(t.row[c.key]) || '—'}</Td>)}
                        <Td>{s(t.row.div)}</Td><Td>{s(t.row.pod_PrdTyp).toUpperCase()}</Td><Td>{s(t.row.terms).toUpperCase()}</Td><Td>{s(t.row.stage).toUpperCase()}</Td>
                        <Td right>{fmt(t.base)}</Td>
                        {/* CHA-440 — the TRUE piece count. On a prepack line pod_qty is PACKS, so the
                            title spells out the pack maths rather than leaving a bare number that looks
                            like pieces (the CHA-433 defect, on a different surface). */}
                        <Td right>
                          <span title={t.mpQty > 1 ? `${t.packs} packs x ${t.mpQty} per pack` : 'not a prepack line'}>
                            {t.units != null ? t.units.toLocaleString('en-US') : '—'}
                            {t.mpQty > 1 ? <span className="ml-1 text-[10px] text-slate-400">MP</span> : null}
                          </span>
                        </Td>
                        <Td right>{t.duty != null ? `${t.duty}%` : '—'}</Td><Td right>{t.tar != null ? `${t.tar}%` : '—'}</Td>
                        <Td right>{fmt(t.expected)}</Td><Td right>{fmt(t.actual)}</Td>
                        <Td right>{fmt(t.variance)}</Td>
                        <Td right>{t.relVar != null && Number.isFinite(t.relVar) ? `${(t.relVar * 100).toFixed(2)}%` : '—'}</Td>
                        <Td>
                          <span className="inline-flex flex-wrap items-center gap-1">
                            <StatusBadge status={t.status} reason={t.reason} />
                            {t.flags && t.flags.length > 0 && (
                              <Flag size={12} className="text-amber-600" title={t.flags.map((fl) => fl.label).join(' · ')} />
                            )}
                            <ChecksInline checks={t.checks} />
                          </span>
                        </Td>
                      </tr>
                      {open && canExpand && (
                        <tr>
                          <td colSpan={COL_SPAN} className="bg-slate-50 px-3 py-3 sm:px-4">
                            <Drilldown t={t} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr><td colSpan={COL_SPAN} className="px-3 py-8 text-center text-slate-400">No rows for this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Excluded / out-of-scope detail */}
          {(evaluated.dateExcluded.length > 0 || evaluated.skipped.length > 0) && (
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer">
                {evaluated.dateExcluded.length} excluded ({dateField} before {SP27_CUTOFF_ISO}) · {evaluated.skipped.length} out of scope
              </summary>
              <ul className="mt-2 space-y-0.5 pl-4 list-disc">
                {summarizeReasons(evaluated.dateExcluded).map((g) => <li key={g.reason}>{g.count}× — {g.reason}</li>)}
                {summarizeReasons(evaluated.skipped).map((g) => <li key={g.reason}>{g.count}× — {g.reason}</li>)}
              </ul>
              {/* CHA-251: term-validity rules have SEPARATE scopes, so some findings land on rows that are
                  out-of-scope for the landed-cost test (not shown per-row above). They ARE in the summary
                  counts; call them out here so they're attributable rather than invisible. */}
              {(() => {
                const oos = summarizeChecks(evaluated.skipped);
                const bits = [
                  oos.RA && `R-A ${oos.RA}`, oos.RB && `R-B ${oos.RB}`, oos.RC && `R-C ${oos.RC}`,
                  oos.dataIncomplete && `data-incomplete ${oos.dataIncomplete}`,
                  (oos.red || oos.yellow || oos.green || oos.strongGreen || oos.discountError) &&
                    `discount ${oos.red + oos.yellow + oos.green + oos.strongGreen + oos.discountError}`,
                ].filter(Boolean);
                return bits.length ? (
                  <p className="mt-2 text-amber-700">
                    CHA-251: {bits.join(' · ')} finding(s) fall on out-of-scope rows (counted in the checks summary above, not drillable individually).
                  </p>
                ) : null;
              })()}
            </details>
          )}
        </>
      )}
    </div>
  );
}

function defaultChips() {
  return { type: [...CHIP_PROD_TYPES], terms: [...CHIP_TERMS], office: [...CHIP_OFFICES], div: [...CHIP_DIVISIONS], stage: [...CHIP_STAGES] };
}

function ChipGroup({ label, group, values, sel, onToggle }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-slate-400 w-12 shrink-0">{label}</span>
      {values.map((v) => {
        const on = sel.includes(v);
        return (
          <button
            key={v}
            onClick={() => onToggle(group, v)}
            className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
              on ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'
            }`}
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

// §8 worked-out-math drilldown: three-number strip + per-component spec-calc vs engine, color-coded,
// plain-English summary. Δ = engine − spec (+higher / −lower).
function Drilldown({ t }) {
  const tone = {
    main: 'bg-[#FCEBEB]', small: 'bg-[#FAEEDA]', match: '', na: '',
  };
  return (
    <div className="space-y-3">
      {/* Three-number strip */}
      <div className="flex flex-wrap gap-3">
        <BigNum label="Spec total" value={`$${fmt3(t.expected)}`} />
        <BigNum label="Engine (n600_cost)" value={`$${fmt3(t.actual)}`} />
        <BigNum
          label="Variance"
          value={`$${fmt3(t.variance)} · ${t.relVar != null && Number.isFinite(t.relVar) ? (t.relVar * 100).toFixed(2) : '—'}%`}
          badge={t.status}
        />
      </div>

      {t.flags && t.flags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {t.flags.map((fl) => (
            <span key={fl.key} className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-0.5">
              <Flag size={11} /> {fl.label}
            </span>
          ))}
        </div>
      )}

      {t.checks && <ChecksPanel checks={t.checks} />}

      <div className="overflow-x-auto">
        <table className="text-xs min-w-full">
          <thead className="text-slate-500">
            <tr>
              <th className="px-3 py-1 text-left font-medium">Component</th>
              <th className="px-3 py-1 text-left font-medium">Spec calculation</th>
              <th className="px-3 py-1 text-right font-medium">Engine</th>
              <th className="px-3 py-1 text-right font-medium">Δ (engine − spec)</th>
            </tr>
          </thead>
          <tbody>
            {t.components.map((c) => (
              <tr key={c.key} className={tone[c.gapClass] || ''}>
                <td className="px-3 py-1 text-slate-700 whitespace-nowrap">
                  {cleanLabel(c.label)}
                  {c.gapClass === 'main' && <span className="ml-1 text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1">main gap</span>}
                  {c.gapClass === 'small' && <span className="ml-1 text-[10px] font-semibold text-amber-800 bg-amber-100 rounded px-1">small gap</span>}
                </td>
                <td className={`px-3 py-1 ${c.gapClass === 'main' ? 'text-red-700' : 'text-slate-600'}`}>{c.calc}</td>
                <td className={`px-3 py-1 text-right tabular-nums ${c.gapClass === 'main' ? 'text-red-700' : 'text-slate-500'}`}>
                  {c.engine == null ? '—' : `$${fmt3(c.engine)} (${c.engineField})`}
                </td>
                <td className="px-3 py-1 text-right tabular-nums">
                  {!c.comparable ? '—'
                    : Math.abs(c.delta) < GAP_EPS
                      ? <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 size={12} /> match</span>
                      : <span className={c.gapClass === 'main' ? 'text-red-600 font-medium' : 'text-amber-700'}>{c.delta < 0 ? '−' : '+'}${fmt3(Math.abs(c.delta))}</span>}
                </td>
              </tr>
            ))}
            <tr className="font-semibold border-t border-slate-300">
              <td className="px-3 py-1">Total</td>
              <td className="px-3 py-1 text-slate-600">sum of components = ${fmt3(t.expected)}</td>
              <td className="px-3 py-1 text-right tabular-nums">${fmt3(t.actual)} (n600_cost)</td>
              <td className="px-3 py-1 text-right tabular-nums">{t.variance < 0 ? '−' : '+'}${fmt3(Math.abs(t.variance))}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-600 bg-white border border-slate-200 rounded px-3 py-2">{t.summary}</p>
    </div>
  );
}

function BigNum({ label, value, badge }) {
  return (
    <div className="px-3 py-2 rounded-lg border border-slate-200 bg-white">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
        {label}
        {badge === 'pass' && <span className="text-green-700">· Pass</span>}
        {badge === 'fail' && <span className="text-red-700">· Fail</span>}
      </div>
      <div className="text-base font-semibold text-slate-800 tabular-nums">{value}</div>
    </div>
  );
}

// Build + download a CSV of the (filtered) rows. One row per validated row; column order per CHA-151.
function exportCsv(rows) {
  if (!rows || rows.length === 0) return;
  const comp = (t, key) => t.components?.find((c) => c.key === key);
  const specOf = (t, key) => { const c = comp(t, key); return c ? c.spec : ''; };
  const engOf = (t, key) => { const c = comp(t, key); return c && c.engine != null ? c.engine : ''; };
  const deltaOf = (t, key) => { const c = comp(t, key); return c && c.comparable ? c.delta : ''; };
  const deltaPct = (t, key) => {
    const c = comp(t, key);
    if (!c || !c.comparable || !(Math.abs(t.actual) > 0)) return '';
    return ((c.delta / Math.abs(t.actual)) * 100).toFixed(3);
  };
  const hasFlag = (t, key) => (t.flags || []).some((f) => f.key === key);
  const r = (t) => t.row;

  const headers = [
    // CHA-313 (operator): warehouse + shipment identifiers ride the export beside due_date.
    'prod_num', 'sty', 'clr', 'div', 'terms', 'stage', 'pod_PrdTyp', 'due_date', 'warehouse_shp2', 'comm_inv', 'shipment_num',
    'units', 'packs', 'mp_qty', 'nFOBHA', 'n300_cost', 'mst_qty', 'n301_frt', 'n305_Ifrt', 'n304_Afrt', 'n401_DutyPct', 'n316_TarPct',
    'spec_CostBase', 'spec_Duty', 'spec_Tariff', 'spec_FRT', 'spec_IFRT', 'spec_AFRT', 'spec_QC', 'spec_Commission', 'spec_Total',
    'engine_n500_Duty', 'engine_n317_Tariff', 'engine_n505_AgtCom', 'engine_n501_AgtPct', 'engine_n600_cost',
    'var_Duty_$', 'var_Duty_%', 'var_Tariff_$', 'var_Tariff_%', 'var_Commission_$', 'var_Commission_%',
    'var_Total_$', 'var_Total_%',
    'result',
    'flag_15pct_discount', 'flag_commission_rate_mismatch', 'flag_dimpt_commission_surfaced',
  ];
  const lines = rows.map((t) => [
    s(r(t).prod_num), s(r(t).sty), s(r(t).clr), s(r(t).div), s(r(t).terms), s(r(t).stage), s(r(t).pod_PrdTyp), s(r(t).due_date), s(r(t).shp2), s(r(t).comm_inv), s(r(t).shipment_num),
    s(t.units), s(t.packs), s(t.mpQty), s(r(t).nFOBHA), s(r(t).n300_cost), s(r(t).mst_qty), s(r(t).n301_frt), s(r(t).n305_Ifrt), s(r(t).n304_Afrt), s(r(t).n401_DutyPct), s(r(t).n316_TarPct),
    // spec_CostBase: CPLAN uses the 'fobha' key (now FOB × (1 − HA%), CHA-436); DUTYP/DIMPT use 'base'
    specOf(t, 'fobha') !== '' ? specOf(t, 'fobha') : specOf(t, 'base'),
    specOf(t, 'duty'), specOf(t, 'tariff'), specOf(t, 'frt'), specOf(t, 'ifrt'), specOf(t, 'afrt'), specOf(t, 'qc'), specOf(t, 'commission'), t.expected,
    engOf(t, 'duty'), engOf(t, 'tariff'), engOf(t, 'commission'), s(r(t).n501_AgtPct), t.actual,
    deltaOf(t, 'duty'), deltaPct(t, 'duty'), deltaOf(t, 'tariff'), deltaPct(t, 'tariff'), deltaOf(t, 'commission'), deltaPct(t, 'commission'),
    t.variance, (t.relVar != null && Number.isFinite(t.relVar) ? (t.relVar * 100).toFixed(3) : ''),
    t.status === 'pass' ? 'Pass' : t.status === 'fail' ? 'Fail' : 'Error',
    hasFlag(t, 'fs15'), hasFlag(t, 'commrate'), hasFlag(t, 'dimpt-comm'),
  ]);

  const esc = (v) => {
    const str = String(v ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers, ...lines].map((row) => row.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `cost-sheet-tester-results_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function fmt(n, dp = 2) {
  return typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : '—';
}
function fmt3(n) {
  return typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : '—';
}

// Group reasons (collapsing the variable bits) for a compact summary.
function summarizeReasons(items) {
  const map = new Map();
  items.forEach(({ reason }) => map.set(reason, (map.get(reason) || 0) + 1));
  return [...map.entries()].map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count);
}

// ── CHA-251 presentation ────────────────────────────────────────────────────────────────────────
// Severity legend — THREE visually distinct problem states: red = hard error / hard invalid ·
// amber = hard warning · grey = data-incomplete (deliberately NOT red — a data gap must never read as a
// discount failure). The discount "good" bands (green / strong-green) sit outside the problem legend.
const DISCOUNT_BAND_STYLE = {
  'error': { cls: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
  'red': { cls: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
  'yellow': { cls: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
  'green': { cls: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-500' },
  'strong-green': { cls: 'bg-emerald-100 text-emerald-900 border-emerald-400 font-semibold', dot: 'bg-emerald-600' },
  'data-incomplete': { cls: 'bg-slate-200 text-slate-600 border-slate-400', dot: 'bg-slate-400' },
};
const RULE_STYLE = {
  invalid: 'bg-red-100 text-red-800 border-red-300',
  warning: 'bg-amber-100 text-amber-800 border-amber-300',
};
const discountShort = (band, d) => {
  if (band === 'data-incomplete') return 'data incomplete';
  if (band === 'error') return 'FOBHA>FOB';
  return `${(d * 100).toFixed(1)}%`;
};

// Compact inline badges for the variance-table Result cell (full text in the title tooltip + drilldown).
function ChecksInline({ checks }) {
  if (!checks) return null;
  const { discount, termRules } = checks;
  const bandStyle = discount ? DISCOUNT_BAND_STYLE[discount.band] : null;
  return (
    <>
      {discount && (
        <span
          title={discount.label}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] ${bandStyle.cls}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${bandStyle.dot}`} />
          {discountShort(discount.band, discount.d)}
          {discount.review && <span className="font-semibold" title="review flag">★</span>}
        </span>
      )}
      {termRules.map((tr) => (
        <span key={tr.rule} title={tr.label} className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] ${RULE_STYLE[tr.severity]}`}>
          {tr.rule}
        </span>
      ))}
    </>
  );
}

// Full CHA-251 panel in the row drilldown — the discount computation + every term-validity finding.
function ChecksPanel({ checks }) {
  const { discount, termRules } = checks;
  const bandStyle = discount ? DISCOUNT_BAND_STYLE[discount.band] : null;
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Discount &amp; term-validity checks (CHA-251)</div>
      {discount && (
        <div className={`text-xs rounded border px-2 py-1.5 ${bandStyle.cls}`}>
          <div className="flex items-center gap-1.5 font-medium">
            <span className={`w-2 h-2 rounded-full ${bandStyle.dot}`} /> {discount.label}
          </div>
          {discount.d != null && (
            <div className="mt-0.5 text-[11px] opacity-90 tabular-nums">
              {/* CHA-451 (Codex) — show the value the discount was actually measured against. Rendering
                  FOBHA while the maths used fs_cost produced operands that contradict their own result:
                  "(FOB $1.720 − FOBHA $1.668) / FOB $1.720 = 20.0%" reads as 3% and claims 20%. */}
              {`(FOB $${fmt3(discount.fob)} − ${discount.fsOn ? 'FS cost' : 'FOBHA'} $${fmt3(discount.customs)}) / FOB $${fmt3(discount.fob)} = ${(discount.d * 100).toFixed(1)}%`}
            </div>
          )}
        </div>
      )}
      {termRules.map((tr) => (
        <div key={tr.rule} className={`text-xs rounded border px-2 py-1.5 ${RULE_STYLE[tr.severity]}`}>{tr.label}</div>
      ))}
      {!discount && termRules.length === 0 && (
        <div className="text-xs text-slate-400">No discount-scope or term-validity findings for this row.</div>
      )}
    </div>
  );
}

// CHA-251 summary strip + the three-severity legend. Analysis-only — every number is a FLAG count.
// CHA-311 — module-level pill button (a component defined INSIDE ChecksSummary would remount on every
// render — losing keyboard focus on each click). pressed=undefined renders a plain non-toggle pill.
function CheckPill({ label, v, style, pressed, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] ${style} ${onClick ? 'cursor-pointer hover:shadow-sm' : ''} ${pressed ? 'ring-2 ring-slate-500 ring-offset-1 font-semibold' : ''}`}
    >
      {label} <span className="font-semibold tabular-nums">{v}</span>
    </button>
  );
}

function ChecksSummary({ c, active, onSelect }) {
  if (!c) return null;
  const anyDiscount = c.red || c.yellow || c.green || c.strongGreen || c.discountError || c.dataIncomplete || c.fsAtSpec15;
  const anyRule = c.RA || c.RB || c.RC;
  if (!anyDiscount && !anyRule) return null;
  // CHA-311 — each pill is a toggle filter (click zooms the table; click again clears). The discount
  // check only exists on CPLAN FS/FS3 rows → red/yellow IS the "First Sale, low discount" zoom.
  const isActive = (f) => !!(active && active.kind === f.kind && active.value === f.value);
  // Plain function (NOT an inline component): returns CheckPill elements directly, so the buttons keep
  // their identity across renders — an inline component type remounts them every render (focus loss).
  const pill = (label, v, style, filter) => (
    <CheckPill key={label} label={label} v={v} style={style} pressed={filter ? isActive(filter) : undefined}
      onClick={filter && onSelect ? () => onSelect(filter) : undefined} />
  );
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="text-xs font-semibold text-slate-600">Discount &amp; term-validity checks (CHA-251) — click a pill to zoom the table (click again to clear)</div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-slate-400 w-16 shrink-0">Discount</span>
        {pill('red <7%', c.red, DISCOUNT_BAND_STYLE.red.cls, { kind: 'band', value: 'red' })}
        {pill('yellow 7–12%', c.yellow, DISCOUNT_BAND_STYLE.yellow.cls, { kind: 'band', value: 'yellow' })}
        {pill('green 12–20%', c.green, DISCOUNT_BAND_STYLE.green.cls, { kind: 'band', value: 'green' })}
        {pill('strong-green >20% ★', c.strongGreen, DISCOUNT_BAND_STYLE['strong-green'].cls, { kind: 'band', value: 'strong-green' })}
        {pill('FOBHA>FOB err', c.discountError, DISCOUNT_BAND_STYLE.error.cls, { kind: 'band', value: 'error' })}
        {pill('data-incomplete', c.dataIncomplete, DISCOUNT_BAND_STYLE['data-incomplete'].cls, { kind: 'band', value: 'data-incomplete' })}
        {pill('shipped @15% spec ⚠', c.fsAtSpec15, RULE_STYLE.warning, { kind: 'fsSpec', value: 'fsAtSpec15' })}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-slate-400 w-16 shrink-0">Terms</span>
        {pill('R-A invalid', c.RA, RULE_STYLE.invalid, { kind: 'rule', value: 'R-A' })}
        {pill('R-B invalid', c.RB, RULE_STYLE.invalid, { kind: 'rule', value: 'R-B' })}
        {pill('R-C warning', c.RC, RULE_STYLE.warning, { kind: 'rule', value: 'R-C' })}
      </div>
      {active && (
        <div className="text-[11px] text-slate-500">
          Zoomed on the tested table — pill counts also include out-of-scope (skipped) rows, which have no variance row to show.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 pt-1 border-t border-slate-100">
        <span className="font-medium text-slate-400">Legend:</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> hard error / invalid</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> hard warning</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" /> data-incomplete (not a discount failure)</span>
      </div>
    </div>
  );
}

function StatusBadge({ status, reason }) {
  if (status === 'pass') return <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={13} /> Pass</span>;
  if (status === 'fail') return <span className="inline-flex items-center gap-1 text-red-700"><XCircle size={13} /> Fail</span>;
  return <span className="inline-flex items-center gap-1 text-amber-700" title={reason}><AlertTriangle size={13} /> Data error</span>;
}

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 font-medium whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, right }) => (
  <td className={`px-3 py-1.5 whitespace-nowrap text-slate-700 ${right ? 'text-right tabular-nums' : ''}`}>{children}</td>
);

function SummaryCard({ label, value, tone = 'slate', onClick, active }) {
  const tones = { slate: 'text-slate-700', green: 'text-green-700', red: 'text-red-700', amber: 'text-amber-700' };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left px-3 py-2 rounded-lg border bg-white ${active ? 'border-orange-400 ring-1 ring-orange-200' : 'border-slate-200'} ${onClick ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </button>
  );
}
